import React, { useEffect, useRef, useState } from "react";
import { publicGet, publicPost } from "../admin/api";
import { loadTurnstile, turnstileTheme } from "../lib/turnstile";
import PrivacyNotice from "../components/brand/PrivacyNotice";

export default function EarlyAccess() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ email: "", company: "", role: "", mcp_servers: "", website: "" });
  const [token, setToken] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const widget = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => { publicGet("/public/waitlist-config").then(setCfg).catch((ex) => setErr(ex.message)); }, []);
  useEffect(() => {
    if (!cfg || !cfg.turnstile_enabled || !cfg.turnstile_site_key) return undefined;
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !widget.current) return;
      widgetId.current = ts.render(widget.current, {
        sitekey: cfg.turnstile_site_key, theme: turnstileTheme(),
        callback: (t) => setToken(t), "expired-callback": () => setToken(""), "error-callback": () => setToken(""),
      });
    }).catch((ex) => setErr(ex.message));
    return () => { cancelled = true; };
  }, [cfg]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await publicPost("/public/waitlist", { ...form, email: form.email.trim(), turnstile_token: token });
      setDone(true);
    } catch (ex) {
      setErr(ex.message);
      if (window.turnstile && widgetId.current != null) { window.turnstile.reset(widgetId.current); setToken(""); }
    } finally { setBusy(false); }
  };
  const lim = cfg?.limits || { email_max: 254, company_max: 200, role_max: 120, mcp_servers_max: 2000 };
  const needsToken = !!cfg?.turnstile_enabled && !token;

  return (
    <section data-testid="early-access-page" className="space-y-6">
      <h1 className="hs-display text-3xl md:text-4xl">Get early access</h1>
      <p className="text-muted hs-prose">
        We are opening HerdSafe to a small number of teams that run MCP servers or build agents on them. Tell us a little
        about your team and we will be in touch. We use your details only to contact you about HerdSafe.
      </p>
      {done ? (
        <div data-testid="early-access-done" role="status" className="hs-card max-w-2xl">
          <span className="hs-badge hs-badge-safe mr-2">Received</span>
          Thanks, you are on the list. We will email you.
        </div>
      ) : (
        <form onSubmit={submit} data-testid="early-access-form" className="hs-card max-w-2xl space-y-4">
          <div>
            <label className="hs-label" htmlFor="ea-email">Work email</label>
            <input id="ea-email" data-testid="ea-email" type="email" required maxLength={lim.email_max} className="hs-input"
                   autoComplete="email" value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className="hs-label" htmlFor="ea-company">Company</label>
            <input id="ea-company" data-testid="ea-company" maxLength={lim.company_max} className="hs-input"
                   autoComplete="organization" value={form.company} onChange={set("company")} />
          </div>
          <div>
            <label className="hs-label" htmlFor="ea-role">Role</label>
            <input id="ea-role" data-testid="ea-role" maxLength={lim.role_max} className="hs-input"
                   autoComplete="organization-title" value={form.role} onChange={set("role")} />
          </div>
          <div>
            <label className="hs-label" htmlFor="ea-servers">What MCP servers does your team use?</label>
            <textarea id="ea-servers" data-testid="ea-servers" rows={4} maxLength={lim.mcp_servers_max} className="hs-input"
                      value={form.mcp_servers} onChange={set("mcp_servers")} />
            <span className="hs-hint">Names or links are fine. Please do not include keys or other secrets.</span>
          </div>
          {/* Honeypot: invisible to people, tempting to bots. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }}>
            <label>Website<input tabIndex={-1} autoComplete="off" data-testid="ea-hp" value={form.website} onChange={set("website")} /></label>
          </div>
          {cfg?.turnstile_enabled && <div ref={widget} data-testid="ea-turnstile" />}
          {err && <div data-testid="ea-error" role="alert" className="hs-text-danger">{err}</div>}
          <button data-testid="ea-submit" type="submit" disabled={busy || !cfg || needsToken} className="hs-btn hs-btn-primary">
            {busy ? "Sending…" : "Request early access"}
          </button>
          <PrivacyNotice />
        </form>
      )}
    </section>
  );
}
